import React, { useState } from 'react';
import { MODES } from './modes';
import ProgressRing from './ProgressRing';
import { IconHome, IconBolt, IconChat, IconBook, IconBars, IconGear, IconPalette, IconShield, IconChevronsLeft, IconChevronsRight, IconCols, IconPerson } from './icons';

// Small logout glyph (door + arrow); the icon set has no logout icon.
const IconLogout = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

/*
 * The five places a learner goes. Each answers one question:
 *   Home           what should I do now?
 *   Practice       can I do it?
 *   Interviews     can I demonstrate it?
 *   Knowledge Hub  what do I want to understand and remember?
 *   Progress       how am I doing?
 * Individual modes (DSA, MCQ, Theory…) live INSIDE Practice / Interviews and
 * only show as a sub-list while the learner is in that area.
 */
const INTERVIEW_MODES = ['interviewer', 'rapidfire'];
const PRACTICE_MODES = ['dsa', 'mcq', 'theory', 'flashcards', 'blitz'];

/** Which primary area a screen (and, in chat, a mode) belongs to. */
export const areaOf = (screen, mode) => {
  if (screen === 'home') return 'home';
  if (screen === 'chat') return INTERVIEW_MODES.includes(mode) ? 'interviews' : 'practice';
  if (['practice', 'case', 'rapid', 'run', 'settings', 'plan', 'nav', 'blitz'].includes(screen)) return 'practice';
  if (['interviews', 'ivsetup', 'ivhistory', 'written', 'discover', 'voice', 'rapidfire', 'report'].includes(screen)) return 'interviews';
  if (['hub', 'hub2', 'path'].includes(screen)) return 'hub';
  if (screen === 'memory') return 'progress';
  if (screen === 'profile') return 'settings';
  return null;
};

export const PRIMARY_NAV = [
  { id: 'home', label: 'Home', Icon: IconHome },
  { id: 'practice', label: 'Practice', Icon: IconBolt },
  { id: 'interviews', label: 'Interviews', Icon: IconChat },
  { id: 'hub', label: 'Knowledge Hub', Icon: IconBook },
  { id: 'progress', label: 'Progress', Icon: IconBars },
];

/*
 * Sidebar — the app's primary navigation.
 *
 * Three widths, owned by NanoApp so the choice survives a reload:
 *   full     the 248px panel (default)
 *   rail     `collapsed` — icons only, labels as tooltips (the toggle is the
 *            chevron beside the logo, or Ctrl/Cmd+B)
 *   hidden   NanoApp unmounts this and shows the edge handle instead
 *            ("Hide sidebar" in the user menu)
 *
 * Every row keeps its `title`, so the rail stays usable without labels.
 */
const Sidebar = ({ screen, activeMode, onSelectMode, onHome, onOpenHome, onBrand, onOpenHub, onOpenMemory, onOpenNav, mcqPanel, userName = 'Arjun Patel', userEmail = 'arjun@mail.com', onSignOut, onOpenProfile, onOpenInterviews, onOpenPractice, onEnterCreator, onEnterAdmin, avatar = '', collapsed = false, onToggleCollapse, onHide }) => {
  const initials = (userName || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const [userMenu, setUserMenu] = useState(false);
  // activeMode is only set on the chat screen; the voice/rapidfire/blitz rooms
  // are their own screens, so map those back to their mode for highlighting.
  const liveMode = activeMode || ({ voice: 'interviewer', rapidfire: 'rapidfire', blitz: 'blitz' })[screen] || null;
  const area = areaOf(screen, liveMode);

  // The menu is a popover inside a scrolling 68px column, so it cannot show in
  // the rail — open the panel first, then the menu, in one click.
  const toggleUserMenu = () => {
    if (collapsed && !userMenu) { onToggleCollapse?.(); setUserMenu(true); return; }
    setUserMenu((o) => !o);
  };

  const OPEN = {
    home: onOpenHome,
    practice: onOpenPractice || onHome,   // the Practice workspace (falls back to the planner)
    interviews: onOpenInterviews,
    hub: onOpenHub,
    progress: onOpenMemory,    // the learner's memory map: strengths, weak spots, trend
  };

  // One row: icon always, label only when the panel is open.
  const navBtn = ({ key, label, Icon, active, onClick, iconSize = 18 }) => (
    <button
      key={key}
      className={`nano-nav-item${active ? ' active' : ''}`}
      onClick={onClick}
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
    >
      <Icon size={iconSize} />
      <span className="nano-nav-txt">{label}</span>
    </button>
  );

  // A quiet indented row inside the active area — text only, never primary.
  const subBtn = ({ key, label, active, onClick }) => (
    <li key={key}>
      <button type="button" className={`nano-subnav-item${active ? ' active' : ''}`}
        onClick={onClick} aria-current={active ? 'page' : undefined}>
        {label}
      </button>
    </li>
  );

  const practiceSub = [
    subBtn({ key: 'plan', label: 'Plan a session', active: screen === 'settings' || screen === 'plan', onClick: onHome }),
    subBtn({ key: 'nav', label: 'DSA problems', active: screen === 'nav', onClick: onOpenNav }),
    ...PRACTICE_MODES.filter((id) => MODES[id]).map((id) => subBtn({
      key: id, label: MODES[id].label, active: liveMode === id && screen !== 'settings', onClick: () => onSelectMode(id),
    })),
  ];

  return (
    <aside id="nano-sidebar" className={`nano-sidebar${collapsed ? ' is-rail' : ''}`}
      aria-label="Main navigation">
      <div className="nano-side-head">
        <button className="nano-brand" onClick={onBrand} type="button" title="Prepare Up — Home">
          <span className="nano-brand-logo" aria-hidden="true">P</span>
          <span className="nano-brand-name">Prepare Up</span>
        </button>
        <button
          className="nano-side-toggle"
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse to icons (Ctrl+B)'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar to icons'}
          aria-expanded={!collapsed}
          aria-controls="nano-sidebar"
        >
          {collapsed ? <IconChevronsRight size={17} /> : <IconChevronsLeft size={17} />}
        </button>
      </div>

      <nav className="nano-primary-nav" aria-label="Primary">
        {PRIMARY_NAV.map(({ id, label, Icon }) => (
          <React.Fragment key={id}>
            {navBtn({ key: id, label, Icon, active: area === id, onClick: OPEN[id] })}
            {/* the Practice page has its own mode bar, so the sub-list shows only inside a session */}
            {!collapsed && area === id && id === 'practice' && !['practice', 'case', 'rapid', 'run'].includes(screen) && !['flashcards', 'mcq', 'theory'].includes(liveMode) && (
              <ul className="nano-subnav" aria-label="Practice modes">{practiceSub}</ul>
            )}
            {/* Interviews has no sub-list: the type (video, voice, written) is chosen on the Interviews page */}
          </React.Fragment>
        ))}
      </nav>

      {mcqPanel && !collapsed && (
        <div className="nano-side-ctx">
          <div className="nano-nav-label">MCQ Practice</div>
          <div className="nano-ctx-card">
            <div className="nano-mcq-progress">
              <ProgressRing value={mcqPanel.solvedPct} color="#f59e0b" />
              <div className="nano-mcq-progress-info">
                <div className="big">{mcqPanel.answeredCount} / {mcqPanel.total} solved</div>
                <div className="row"><span className="dot ok" /> Correct <b>{mcqPanel.correctCount}</b></div>
                <div className="row"><span className="dot no" /> Incorrect <b>{mcqPanel.incorrectCount}</b></div>
                <div className="row"><span className="dot un" /> Unattempted <b>{mcqPanel.unattempted}</b></div>
              </div>
            </div>
          </div>
          <div className="nano-ctx-card">
            <div className="lbl">Question Navigator</div>
            <div className="nano-mcq-nav">
              {Array.from({ length: mcqPanel.total }).map((_, j) => {
                const cls = [
                  'nano-mcq-navdot',
                  mcqPanel.answers[j] !== undefined ? 'answered' : '',
                  mcqPanel.marked.includes(j) ? 'review' : '',
                  j === mcqPanel.idx ? 'current' : '',
                ].join(' ').replace(/\s+/g, ' ').trim();
                return <button key={j} className={cls} onClick={() => mcqPanel.goTo(j)} type="button">{j + 1}</button>;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="nano-side-foot">
        {navBtn({ key: 'settings', label: 'Settings', Icon: IconGear, active: area === 'settings', onClick: onOpenProfile })}
        <div className="nano-user" onClick={toggleUserMenu} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleUserMenu(); } }}
          title={collapsed ? `${userName} — account menu` : undefined}
          aria-label={`${userName} — profile and account menu`}
          aria-haspopup="menu" aria-expanded={userMenu}>
          {userMenu && (
            <div className="nano-user-menu" role="menu" onClick={(e) => e.stopPropagation()}>
              <button type="button" role="menuitem"
                onClick={() => { setUserMenu(false); onOpenProfile?.(); }}>
                <IconPerson size={15} /> Profile
              </button>
              <button type="button" role="menuitem"
                onClick={() => { setUserMenu(false); onOpenProfile?.(); }}>
                <IconGear size={15} /> Settings
              </button>
              {onEnterCreator && (
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenu(false); onEnterCreator(); }}>
                  <IconPalette size={15} /> Creator mode
                </button>
              )}
              {onEnterAdmin && (
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenu(false); onEnterAdmin(); }}>
                  <IconShield size={15} /> Admin portal
                </button>
              )}
              {onHide && (
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenu(false); onHide(); }}>
                  <IconCols size={15} /> Hide sidebar
                </button>
              )}
              <button type="button" role="menuitem" className="danger"
                onClick={() => { setUserMenu(false); onSignOut?.(); }}>
                <IconLogout size={15} /> Log out
              </button>
            </div>
          )}
          {avatar
            ? <img className="nano-user-av img" src={avatar} alt="" style={{ objectFit: 'cover' }} />
            : <div className="nano-user-av">{initials}</div>}
          <div className="nano-user-meta">
            <div className="nano-user-name">{userName}</div>
            <div className="nano-user-mail">{userEmail}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

/*
 * MobileNav — the same five destinations as a bottom bar. The sidebar is
 * hidden below 960px, so this is the only navigation on phones and small
 * tablets; CSS keeps it out of the way on wider screens.
 */
export const MobileNav = ({ screen, mode, onOpenHome, onOpenPractice, onOpenInterviews, onOpenHub, onOpenProgress }) => {
  const area = areaOf(screen, mode);
  const OPEN = { home: onOpenHome, practice: onOpenPractice, interviews: onOpenInterviews, hub: onOpenHub, progress: onOpenProgress };
  const SHORT = { hub: 'Hub' };
  return (
    <nav className="nano-mobile-nav" aria-label="Primary">
      {PRIMARY_NAV.map(({ id, label, Icon }) => (
        <button key={id} type="button" className={`nano-mobile-item${area === id ? ' active' : ''}`}
          onClick={OPEN[id]} aria-label={label} aria-current={area === id ? 'page' : undefined}>
          <Icon size={19} />
          <span aria-hidden="true">{SHORT[id] || label}</span>
        </button>
      ))}
    </nav>
  );
};

export default Sidebar;
